import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Form, Input } from "antd";
import ButtonWrapper from "../../../views/components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";

const { TextArea } = Input;

interface OurMissionVisionProps {
  editingRow: string | null;
  setEditingRow: (value: string | null) => void;
  handleSave: () => void;
  handleCancel: () => void;
  form: any;
  aboutData?: {
    heading: string;
    subtext?: string;
    mission?: {
      heading: string;
      subtext?: string;
      image?: string;
    };

    vision?: {
      heading: string;
      subtext?: string;
      image?: string;
    };
  };

  loading?: boolean;
}

const OurMissionVision: React.FC<OurMissionVisionProps> = ({
  editingRow,
  setEditingRow,
  handleSave,
  handleCancel,
  form,
  aboutData,
  loading,
}) => {
  useEffect(() => {
    if (aboutData) {
      form.setFieldsValue({
        our_mission_vision: {
          heading: aboutData.heading || "",
          subtext: aboutData.subtext || "",
          mission: {
            heading: aboutData?.mission?.heading || "",
            subtext: aboutData.mission?.subtext || "",
            image: aboutData.mission?.image || "",
          },
          vision: {
            heading: aboutData?.vision?.heading || "",
            subtext: aboutData?.vision?.subtext || "",
            image: aboutData?.vision?.image || "",
          },
        },
      });
    }
  }, [aboutData]);

  const rows = [
    { key: "heading", label: "Heading", value: aboutData?.heading },
    { key: "mission", label: "Mission", value: aboutData?.mission?.heading },
    { key: "vision", label: "Vision", value: aboutData?.vision?.heading },
  ];

  return (
    <div>
      {!editingRow && (
        <div className="rounded-2xl overflow-hidden hidden sm:block">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-primary text-white">
                <th className="py-3 px-4 text-left">Section</th>
                <th className="py-3 px-4 text-left">Content</th>
                <th className="py-3 px-4 text-end">Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  className="bg-[#F9F9F9] border-b border-[#e5e5e5]"
                >
                  <td className="py-3 px-4 font-semibold text-gray-700">
                    {row.label}
                  </td>

                  <td className="py-3 px-4 text-gray-700">
                    <span>{row.value || "—"}</span>
                  </td>

                  <td className="py-3 px-4 text-end">
                    <EditOutlined
                      className="cursor-pointer text-primary text-[20px]"
                      onClick={() => setEditingRow(row.key)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!editingRow && (
        <div className="sm:hidden space-y-4">
          {rows.map((row) => (
            <div
              key={row.key}
              className="bg-white rounded-xl shadow-sm border p-4 space-y-2"
            >
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-gray-800">{row.label}</h4>
                <EditOutlined
                  className="cursor-pointer text-primary text-[20px]"
                  onClick={() => setEditingRow(row.key)}
                />
              </div>

              <p className="text-gray-600 text-sm">
                {row.value || "No content added"}
              </p>
            </div>
          ))}
        </div>
      )}

      {editingRow && (
        <div className="bg-[#F9F9F9] rounded-lg p-4 shadow-sm space-y-3 mt-4">
          <h3 className="font-semibold text-lg mb-3 capitalize">
            Edit {editingRow}
          </h3>

          <Form form={form} layout="vertical">
            {editingRow === "heading" && (
              <>
                <Form.Item
                  name={["our_mission_vision", "heading"]}
                  rules={[
                    { required: true, message: "This field is required" },
                  ]}
                >
                  <Input placeholder="Edit heading..." />
                </Form.Item>

                <Form.Item
                  name={["our_mission_vision", "subtext"]}
                  rules={[
                    { required: true, message: "This field is required" },
                  ]}
                >
                  <TextArea rows={3} placeholder="Edit subtext" />
                </Form.Item>
              </>
            )}

            {editingRow === "mission" && (
              <>
                <Form.Item
                  name={["our_mission_vision", "mission", "heading"]}
                  rules={[
                    { required: true, message: "This field is required" },
                  ]}
                >
                  <Input placeholder="Edit heading..." />
                </Form.Item>
                <Form.Item
                  label="Mission Text"
                  name={["our_mission_vision", "mission", "subtext"]}
                  rules={[{ required: true, message: "Text is required" }]}
                >
                  <TextArea rows={3} placeholder="Enter mission text" />
                </Form.Item>
                <Form.Item
                  label="Upload Image"
                  name={["our_mission_vision", "mission", "image"]}
                  rules={[
                    {
                      required: true,
                      message: "Please upload a image",
                    },
                  ]}
                >
                  <UploadFoodImage maxUploadCount={1} />
                </Form.Item>
              </>
            )}

            {editingRow === "vision" && (
              <>
                <Form.Item
                  name={["our_mission_vision", "vision", "heading"]}
                  rules={[
                    { required: true, message: "This field is required" },
                  ]}
                >
                  <Input placeholder="Edit heading..." />
                </Form.Item>
                <Form.Item
                  label="Vision Text"
                  name={["our_mission_vision", "vision", "subtext"]}
                  rules={[{ required: true, message: "Text is required" }]}
                >
                  <TextArea rows={3} placeholder="Enter vision text" />
                </Form.Item>

                <Form.Item
                  label="Upload Image"
                  name={["our_mission_vision", "vision", "image"]}
                  rules={[
                    {
                      required: true,
                      message: "Please upload a image",
                    },
                  ]}
                >
                  <UploadFoodImage maxUploadCount={1} />
                </Form.Item>
              </>
            )}

            <div className="flex gap-3">
              <ButtonWrapper
                type="primary"
                loading={loading}
                onClick={async () => {
                  await handleSave();
                  setEditingRow(null);
                }}
              >
                Save
              </ButtonWrapper>

              <ButtonWrapper
                onClick={() => {
                  handleCancel();
                  setEditingRow(null);
                }}
              >
                Cancel
              </ButtonWrapper>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default OurMissionVision;
